/**
 * Realtime query-key-prefix registry.
 *
 * The persist flush must NOT persist realtime queries (the markets feed
 * etc.) — they carry `staleTime: Infinity` and re-hydrate from the live
 * wire, so a persisted snapshot only restores stale data across reloads.
 *
 * The obvious signal — `query.meta.realtime === true` — is UNRELIABLE for
 * SSR-prefetched queries: the server builds them via `setQueryData` (no
 * meta), `dehydrate` therefore omits meta, and the client only re-applies
 * meta once a hook OBSERVES the query. An SSR-prefetched-but-never-observed
 * feed query (e.g. an inactive sort tab) reaches the flush with no meta and
 * would slip through a meta-only filter.
 *
 * The queryKey, by contrast, is always present and stable. The hook
 * factories (`createHook` / `createListHook`) run at module load for every
 * imported hook, so they register each realtime query's leading
 * string-prefix here eagerly — before any flush. The flush then excludes
 * any query whose leading string-prefix is registered, regardless of meta.
 */

/** Leading run of string segments, e.g. `['markets','list',{…},[…]]` → `markets\0list`. */
function staticPrefixOf(queryKey: readonly unknown[]): string {
  const segments: string[] = [];
  for (const segment of queryKey) {
    if (typeof segment !== 'string') break;
    segments.push(segment);
  }
  return segments.join('\0');
}

const realtimePrefixes = new Set<string>();

/**
 * Register a spec's query-key prefix as realtime, if its entity-key factory
 * reports `meta.realtime === true`. Normalized lists also register the
 * canonical entity prefix because those rows inherit the list's realtime
 * lifecycle even though their cache entries have no query metadata.
 *
 * Safe to call at module-load time for every hook; non-realtime specs are
 * ignored. Best-effort: a factory that throws on empty args is skipped (the
 * meta fast-path still covers it once observed).
 */
export function registerRealtimeSpec(spec: {
  kind: string;
  entityKeys: Record<string, unknown>;
  normalized?: boolean;
  entityQueryKey?: (id: string) => readonly unknown[];
}): void {
  const factory = spec.entityKeys?.[spec.kind] as
    | ((args: unknown) => { queryKey: readonly unknown[]; meta?: { realtime?: boolean } })
    | undefined;
  if (typeof factory !== 'function') return;
  try {
    const built = factory({});
    if (built.meta?.realtime !== true) return;
    const prefix = staticPrefixOf(built.queryKey);
    if (prefix.length > 0) realtimePrefixes.add(prefix);
    if (spec.normalized === true && spec.entityQueryKey !== undefined) {
      const entityPrefix = staticPrefixOf(
        spec.entityQueryKey('__realtime_entity_prefix__').slice(0, -1),
      );
      if (entityPrefix.length > 0) realtimePrefixes.add(entityPrefix);
    }
  } catch {
    // Factory required a concrete arg — skip; the `meta.realtime` fast-path
    // in `shouldDehydrateQuery` still excludes it once a hook observes it.
  }
}

/** True when the query's leading string-prefix belongs to a realtime spec. */
export function isRealtimeQueryKey(queryKey: readonly unknown[]): boolean {
  const key = staticPrefixOf(queryKey);
  for (const prefix of realtimePrefixes) {
    if (key === prefix || key.startsWith(`${prefix}\0`)) return true;
  }
  return false;
}

/** Test-only: reset the registry between cases. */
export function _resetRealtimeRegistryForTest(): void {
  realtimePrefixes.clear();
}
